import type {TFunction} from 'i18next'
import {Tag} from '@/components/ui/tag'
import {type Column} from '@/components/data-table'
import {HelpIcon} from '@/components/help-icon'
import {PaqueteEstadoCelda} from '@/components/bateria/paquete-estado-celda'
import {PersonaAccionesCelda} from '@/components/bateria/persona-acciones-celda'
import {PaginasCelda, PendientesCelda, RiesgoCelda} from '@/components/bateria/persona-celdas'
import type {BateriaPaquete} from '@/hooks/bateria/use-bateria'
import type {LotesResumen} from '@/lib/bateria'

/** La página es dueña del estado y la navegación; los flujos E2E leen estos `data-testid`. */
export function columnasDePersonas({
  t,
  businessId,
  evaluacionId,
  lotes,
  batchRunning,
  readOnly,
  validarPending,
  onIrAUbicar,
  onIrAPendientes,
  onValidar,
  onRevisar,
  onAbrirEnCola,
  onDigitar,
  onVerResultado,
  onVerFicha,
  onImprimir,
  onReprocesar,
  onReasignar,
  onCorregirIdentidad,
  onCambiarForma,
  onRetirar,
  onVerHistorialPaginas,
}: {
  t: TFunction
  businessId: string
  evaluacionId: string
  lotes: LotesResumen
  batchRunning: boolean
  readOnly: boolean
  validarPending: boolean
  onIrAUbicar: () => void
  onIrAPendientes: (paquete: BateriaPaquete) => void
  onValidar: (paquete: BateriaPaquete) => void
  onRevisar: (paquete: BateriaPaquete) => void
  onAbrirEnCola: (paquete: BateriaPaquete) => void
  onDigitar: (paquete: BateriaPaquete) => void
  onVerResultado: (paquete: BateriaPaquete) => void
  onVerFicha: (paquete: BateriaPaquete) => void
  onImprimir: (paquete: BateriaPaquete) => void
  onReprocesar: (paquete: BateriaPaquete) => void
  onReasignar: (paquete: BateriaPaquete) => void
  onCorregirIdentidad: (paquete: BateriaPaquete) => void
  onCambiarForma: (paquete: BateriaPaquete) => void
  onRetirar: (paquete: BateriaPaquete) => void
  onVerHistorialPaginas: (paquete: BateriaPaquete) => void
}): Column<BateriaPaquete>[] {
  return [
    {
      key: 'employee',
      header: t('admin.bateria.detail.employee'),
      // Ancho explícito solo en móvil: sin él, `table-fixed` deja el nombre en cero a 390px.
      width: 'w-40 sm:w-auto',
      cell: (p) => (
        <div className="min-w-0">
          <span className="block truncate font-medium text-foreground">{p.employee_name}</span>
          <span className="block font-mono text-[11px] text-muted-foreground">{p.employee_cedula}</span>
        </div>
      ),
    },
    {
      key: 'forma',
      header: (
        <span className="inline-flex items-center gap-1">
          {t('admin.bateria.detail.forma')}
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('admin.bateria.detail.forma')})}
            testId="paquete-forma-help">
            {t('admin.bateria.detail.formaHelp')}
          </HelpIcon>
        </span>
      ),
      width: 'w-20',
      cell: (p) => <Tag tone="neutral">{t('admin.bateria.formaValue', {forma: p.forma})}</Tag>,
    },
    {
      key: 'estado',
      header: (
        <span className="inline-flex items-center gap-1">
          {t('admin.bateria.detail.estado')}
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('admin.bateria.detail.estado')})}
            testId="paquete-estado-escalera-help">
            {t('admin.bateria.detail.resumenHelp')}
          </HelpIcon>
        </span>
      ),
      width: 'w-44',
      cell: (p) => (
        <PaqueteEstadoCelda paquete={p} lotes={lotes} batchRunning={batchRunning} onIrACuarentena={onIrAUbicar} />
      ),
    },
    {
      key: 'paginas',
      header: t('admin.bateria.detail.paginasHeader'),
      width: 'w-28',
      cell: (p) => <PaginasCelda paquete={p} />,
    },
    {
      key: 'pendientes',
      header: t('admin.bateria.detail.pendientesHeader'),
      width: 'w-28',
      cell: (p) => <PendientesCelda paquete={p} onIr={() => onIrAPendientes(p)} />,
    },
    {
      key: 'riesgo',
      header: (
        <span className="inline-flex items-center gap-1">
          {t('admin.bateria.detail.riesgoHeader')}
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('admin.bateria.detail.riesgoHeader')})}
            testId="paquete-riesgo-help">
            {t('admin.bateria.detail.riesgoHelp')}
          </HelpIcon>
        </span>
      ),
      width: 'w-32',
      cell: (p) => <RiesgoCelda paquete={p} onVerResultado={() => onVerResultado(p)} />,
    },
    {
      key: 'actions',
      header: '',
      width: 'w-44',
      className: 'text-right',
      cell: (p) => (
        <PersonaAccionesCelda
          paquete={p}
          businessId={businessId}
          evaluacionId={evaluacionId}
          lotes={lotes}
          batchRunning={batchRunning}
          readOnly={readOnly}
          validarPending={validarPending}
          onValidar={onValidar}
          onRevisar={onRevisar}
          onAbrirEnCola={onAbrirEnCola}
          onDigitar={onDigitar}
          onVerResultado={onVerResultado}
          onVerFicha={onVerFicha}
          onImprimir={onImprimir}
          onReprocesar={onReprocesar}
          onReasignar={onReasignar}
          onCorregirIdentidad={onCorregirIdentidad}
          onCambiarForma={onCambiarForma}
          onRetirar={onRetirar}
          onVerHistorialPaginas={onVerHistorialPaginas}
        />
      ),
    },
  ]
}
